import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const TRADING_DAYS_PER_YEAR=252;

function finite(v){
  const n=Number(v);
  return v!==null&&v!==undefined&&v!==''&&Number.isFinite(n)?n:null;
}
function round(v,d=2){return Number.isFinite(v)?Number(v.toFixed(d)):null;}
function avg(xs){const a=xs.filter(Number.isFinite);return a.length?a.reduce((x,y)=>x+y,0)/a.length:null;}
function stdev(xs){
  const a=xs.filter(Number.isFinite);
  if(a.length<2)return null;
  const m=avg(a), variance=a.reduce((s,x)=>s+(x-m)*(x-m),0)/(a.length-1);
  return Math.sqrt(variance);
}
function pctChange(a,b){return Number.isFinite(a)&&Number.isFinite(b)&&b!==0?((a/b)-1)*100:null;}
function isoDate(s){return /^\d{4}-\d{2}-\d{2}$/.test(String(s||''));}

function weekKey(dateStr){
  const d=new Date(dateStr+'T00:00:00Z');
  if(Number.isNaN(d.getTime()))return null;
  const x=new Date(Date.UTC(d.getUTCFullYear(),d.getUTCMonth(),d.getUTCDate()));
  const day=x.getUTCDay()||7;
  x.setUTCDate(x.getUTCDate()+4-day);
  const yearStart=new Date(Date.UTC(x.getUTCFullYear(),0,1));
  const week=Math.ceil((((x-yearStart)/86400000)+1)/7);
  return `${x.getUTCFullYear()}-W${String(week).padStart(2,'0')}`;
}

function normalizeRows(payload={}){
  const rows=Array.isArray(payload.rows)?payload.rows:[];
  const seen=new Set(), clean=[];
  for(const r of rows){
    const date=String(r?.date||'');
    if(!isoDate(date)||seen.has(date))continue;
    const close=finite(r.close), volume=finite(r.volume);
    if(close===null||close<=0)continue;
    seen.add(date);
    clean.push(Object.freeze({
      date,
      open:finite(r.open),
      high:finite(r.high),
      low:finite(r.low),
      close,
      volume:volume!==null&&volume>=0?volume:null
    }));
  }
  return Object.freeze(clean.sort((a,b)=>b.date.localeCompare(a.date)));
}

function weeklyCloses(rows){
  /* Newest daily row per ISO week = last available trading session in that week. */
  const byWeek=new Map();
  for(const r of rows){
    const k=weekKey(r.date);
    if(k&&!byWeek.has(k))byWeek.set(k,Object.freeze({week:k,date:r.date,close:r.close}));
  }
  return Object.freeze([...byWeek.values()].sort((a,b)=>b.date.localeCompare(a.date)));
}

function build(payload={},meta={}){
  const rows=normalizeRows(payload);
  const latest=rows[0]||null, prev=rows[1]||null;
  if(!latest){
    return Object.freeze({
      version:VERSION,symbol:payload.symbol||null,status:'MISSING',asOf:null,
      coverage:Object.freeze({dailyRows:0,weeklyCloses:0,has52Week:false,has120WeekMA:false,has30DayVolatility:false}),
      priceContext:null,
      provenance:meta.provenance||null,
      guards:Object.freeze({missingRemainsMissing:true,noBetaInference:true,noLiquidityScoreInference:true,noTradeSignal:true})
    });
  }

  const latestMs=new Date(latest.date+'T00:00:00Z').getTime();
  const cutoff52=latestMs-(364*86400000);
  const rows52=rows.filter(r=>new Date(r.date+'T00:00:00Z').getTime()>=cutoff52);
  const highs52=rows52.map(r=>finite(r.high)).filter(Number.isFinite);
  const lows52=rows52.map(r=>finite(r.low)).filter(Number.isFinite);
  const high52=highs52.length?Math.max(...highs52):null;
  const low52=lows52.length?Math.min(...lows52):null;
  const rangePos=(Number.isFinite(high52)&&Number.isFinite(low52)&&high52>low52)
    ?((latest.close-low52)/(high52-low52))*100:null;
  const drawdown52=Number.isFinite(high52)?pctChange(latest.close,high52):null;

  const weeks=weeklyCloses(rows);
  const first120=weeks.slice(0,120).map(x=>x.close);
  const ma120=first120.length===120?avg(first120):null;
  const ma120FourWeeksAgo=weeks.length>=124?avg(weeks.slice(4,124).map(x=>x.close)):null;
  const ma120SlopePct=(Number.isFinite(ma120)&&Number.isFinite(ma120FourWeeksAgo))
    ?pctChange(ma120,ma120FourWeeksAgo):null;
  const distance120=Number.isFinite(ma120)?pctChange(latest.close,ma120):null;

  const volumes20=rows.slice(0,20).map(r=>r.volume).filter(Number.isFinite);
  const avgVol20=volumes20.length===20?avg(volumes20):null;
  const rvol=(Number.isFinite(latest.volume)&&Number.isFinite(avgVol20)&&avgVol20>0)?latest.volume/avgVol20:null;
  const volumeVs20=Number.isFinite(rvol)?(rvol-1)*100:null;

  const recent10=rows.slice(0,10).map(r=>r.volume).filter(Number.isFinite);
  const prior10=rows.slice(10,20).map(r=>r.volume).filter(Number.isFinite);
  const recent10Avg=recent10.length===10?avg(recent10):null;
  const prior10Avg=prior10.length===10?avg(prior10):null;
  const volumeTrendPct=(Number.isFinite(recent10Avg)&&Number.isFinite(prior10Avg)&&prior10Avg>0)
    ?pctChange(recent10Avg,prior10Avg):null;

  const closes31=rows.slice(0,31).map(r=>r.close);
  const logReturns=[];
  for(let i=0;i<closes31.length-1;i++){
    const newer=closes31[i], older=closes31[i+1];
    if(newer>0&&older>0)logReturns.push(Math.log(newer/older));
  }
  const vol30=logReturns.length===30?stdev(logReturns)*Math.sqrt(TRADING_DAYS_PER_YEAR)*100:null;

  const priceChange1D=prev?pctChange(latest.close,prev.close):null;
  const tradedValue=(Number.isFinite(latest.volume)?latest.close*latest.volume:null);

  const priceVolumeState=
    Number.isFinite(priceChange1D)&&Number.isFinite(volumeVs20)
      ?(priceChange1D>0&&volumeVs20>0?'PRICE_UP_VOLUME_ABOVE_20D':
        priceChange1D<0&&volumeVs20>0?'PRICE_DOWN_VOLUME_ABOVE_20D':
        'MIXED')
      :'MISSING';

  const rangeLocation=Number.isFinite(rangePos)
    ?(rangePos>=90?'UPPER_10_PERCENT':rangePos<=10?'LOWER_10_PERCENT':'MID_RANGE')
    :'MISSING';

  const maPosition=Number.isFinite(distance120)
    ?(distance120>0?'ABOVE_120W_MA':distance120<0?'BELOW_120W_MA':'AT_120W_MA')
    :'MISSING';

  const slopeDirection=Number.isFinite(ma120SlopePct)
    ?(ma120SlopePct>0?'RISING':ma120SlopePct<0?'FALLING':'FLAT')
    :'MISSING';

  const out=Object.freeze({
    version:VERSION,
    symbol:payload.symbol||null,
    status:'READY',
    asOf:latest.date,
    timezone:payload.timezone||null,
    latest:Object.freeze({
      price:round(latest.close,4),
      previousClose:prev?round(prev.close,4):null,
      priceChange1DPct:round(priceChange1D,2),
      volume:latest.volume,
      tradedValue:round(tradedValue,2)
    }),
    range52Week:Object.freeze({
      high:round(high52,4),
      low:round(low52,4),
      positionPct:round(rangePos,1),
      drawdownFromHighPct:round(drawdown52,2),
      location:rangeLocation,
      observations:rows52.length
    }),
    movingAverage120Week:Object.freeze({
      value:round(ma120,4),
      distancePct:round(distance120,2),
      position:maPosition,
      slopePct4Week:round(ma120SlopePct,3),
      slopeDirection,
      weeklyObservations:weeks.length,
      sufficient:first120.length===120
    }),
    volume20Day:Object.freeze({
      current:latest.volume,
      average:round(avgVol20,2),
      rvol:round(rvol,3),
      vsAveragePct:round(volumeVs20,2),
      recent10DayAverage:round(recent10Avg,2),
      prior10DayAverage:round(prior10Avg,2),
      trendPct:round(volumeTrendPct,2)
    }),
    volatility30Day:Object.freeze({
      annualizedPct:round(vol30,2),
      returnObservations:logReturns.length,
      sufficient:logReturns.length===30
    }),
    readouts:Object.freeze({
      priceVolume:priceVolumeState,
      rangeLocation,
      movingAveragePosition:maPosition,
      movingAverageSlope:slopeDirection
    }),
    intentionallyMissing:Object.freeze({
      beta5Y:null,
      liquidityQualitative:null,
      relativeStrength:null,
      fairValue:null,
      valuationMultiples:null
    }),
    coverage:Object.freeze({
      dailyRows:rows.length,
      weeklyCloses:weeks.length,
      has52Week:rows52.length>0&&Number.isFinite(high52)&&Number.isFinite(low52),
      has120WeekMA:first120.length===120,
      has120WeekSlope:weeks.length>=124,
      has20DayVolume:volumes20.length===20,
      has30DayVolatility:logReturns.length===30
    }),
    provenance:meta.provenance||null,
    guards:Object.freeze({
      missingRemainsMissing:true,
      noBetaInference:true,
      noLiquidityScoreInference:true,
      noTradeSignal:true,
      noCapitalPermission:true,
      noUniversalScore:true
    })
  });
  return out;
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const rows=[];
  const start=new Date('2026-08-30T00:00:00Z');
  let trading=0;
  for(let i=0;trading<700;i++){
    const d=new Date(start.getTime()-i*86400000);
    const day=d.getUTCDay(); if(day===0||day===6)continue;
    const n=699-trading;
    rows.push({
      date:d.toISOString().slice(0,10),
      open:100+n*.1,high:102+n*.1,low:98+n*.1,close:101+n*.1,
      volume:1000000+trading*1000
    });
    trading++;
  }
  const p={symbol:'AAA',timezone:'US/Eastern',rows};
  const x=build(p,{provenance:{provider:'TEST'}});
  ok('ready with data',x.status==='READY');
  ok('symbol preserved',x.symbol==='AAA');
  ok('asOf latest row',x.asOf===rows[0].date);
  ok('latest price numeric',x.latest.price===rows[0].close);
  ok('previous close present',x.latest.previousClose===rows[1].close);
  ok('1D change derived',Number.isFinite(x.latest.priceChange1DPct));
  ok('traded value derived',Number.isFinite(x.latest.tradedValue));
  ok('52W high derived',Number.isFinite(x.range52Week.high));
  ok('52W low derived',Number.isFinite(x.range52Week.low));
  ok('52W position derived',Number.isFinite(x.range52Week.positionPct));
  ok('drawdown derived',Number.isFinite(x.range52Week.drawdownFromHighPct));
  ok('120W MA has exact coverage',x.movingAverage120Week.sufficient===true);
  ok('120W MA derived',Number.isFinite(x.movingAverage120Week.value));
  ok('120W distance derived',Number.isFinite(x.movingAverage120Week.distancePct));
  ok('120W slope has coverage',x.coverage.has120WeekSlope===true);
  ok('120W slope derived',Number.isFinite(x.movingAverage120Week.slopePct4Week));
  ok('20D volume average derived',Number.isFinite(x.volume20Day.average));
  ok('RVOL derived',Number.isFinite(x.volume20Day.rvol));
  ok('volume vs average derived',Number.isFinite(x.volume20Day.vsAveragePct));
  ok('volume trend derived',Number.isFinite(x.volume20Day.trendPct));
  ok('30D volatility has exact return coverage',x.volatility30Day.returnObservations===30);
  ok('30D volatility derived',Number.isFinite(x.volatility30Day.annualizedPct));
  ok('beta deliberately missing',x.intentionallyMissing.beta5Y===null);
  ok('liquidity label deliberately missing',x.intentionallyMissing.liquidityQualitative===null);
  ok('relative strength deliberately missing',x.intentionallyMissing.relativeStrength===null);
  ok('fair value deliberately missing',x.intentionallyMissing.fairValue===null);
  ok('provenance retained',x.provenance.provider==='TEST');
  ok('no trade signal guard',x.guards.noTradeSignal===true);
  ok('no capital permission guard',x.guards.noCapitalPermission===true);
  ok('no universal score guard',x.guards.noUniversalScore===true);

  const short=build({symbol:'SHORT',rows:rows.slice(0,25)});
  ok('short data remains ready for available fields',short.status==='READY');
  ok('short 120W MA missing',short.movingAverage120Week.value===null);
  ok('short 120W sufficient false',short.movingAverage120Week.sufficient===false);
  ok('short 30D volatility missing',short.volatility30Day.annualizedPct===null);
  ok('short beta still missing',short.intentionallyMissing.beta5Y===null);

  const empty=build({symbol:'EMPTY',rows:[]});
  ok('empty data missing',empty.status==='MISSING');
  ok('empty price context null',empty.priceContext===null);
  ok('empty coverage zero',empty.coverage.dailyRows===0);

  const dirty=build({symbol:'DIRTY',rows:[
    {date:'2026-08-30',close:'10',volume:'100'},
    {date:'2026-08-30',close:'99',volume:'999'},
    {date:'bad',close:5,volume:10},
    {date:'2026-08-29',close:null,volume:20}
  ]});
  ok('duplicate dates deduped',dirty.coverage.dailyRows===1);
  ok('invalid close excluded',dirty.latest.price===10);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestMarketDailyCanonicalSnapshot=Object.freeze({VERSION,normalizeRows,weeklyCloses,build,selfTest});
})(engineContext);